import React from 'react';
import AlertCircleIcon from '@assets/icons/alert-circle.svg';
import CheckmarkIcon from '@assets/icons/checkmark-circle.svg';
import InfoIcon from '@assets/icons/info-circle.svg';
import { PointsToast, Toast } from '@components/toast';
import type { NotificationInstance } from 'antd/es/notification/interface';

export const TOAST_TYPE = {
    INFO: 'info',
    SUCCESS: 'success',
    ERROR: 'error',
} as const;

const POINTS_TOAST_STYLES = {
    borderRadius: '8px',
    border: 'var(--border-width-sm-1, 1px) solid var(--Neutral-200, #E2E8F0)',
    boxShadow: '0px 4px 4px rgba(80, 86, 110, 0.15), 1px 7px 9.1px rgba(180, 184, 191, 0.15)',
};

const GENERAL_STYLES = {
    padding: '24px',
};

const SECONDS_TO_HIDE = 3;

const getPointsToastBackground = (isBonus: boolean) =>
    isBonus ? '#FFFEF8' : 'linear-gradient(0deg, #F2F8FD 0%, #F2F8FD 100%), #FFF';

const getToastBackground = (type: (typeof TOAST_TYPE)[keyof typeof TOAST_TYPE]): string => {
    switch (type) {
        case TOAST_TYPE.INFO:
            return 'var(--colors-surface-page, #E7F4FF)';
        case TOAST_TYPE.SUCCESS:
            return 'var(--colors-surface-success, #f3fffa)';
        case TOAST_TYPE.ERROR:
            return 'var(--colors-surface-error, #FFF2F2)';
        default:
            return 'var(--colors-surface-primary, #FFF)';
    }
};

export const getToastIcon = (type: (typeof TOAST_TYPE)[keyof typeof TOAST_TYPE]): React.ReactNode => {
    switch (type) {
        case TOAST_TYPE.INFO:
            return <InfoIcon />;
        case TOAST_TYPE.SUCCESS:
            return <CheckmarkIcon />;
        case TOAST_TYPE.ERROR:
            return <AlertCircleIcon />;
        default:
            return null;
    }
};

export const showPointsToast = (
    notify: NotificationInstance, // take from /hooks/useNotify.ts
    title: string,
    description: string,
    pointsEarned: number,
    isBonus = false,
    secondsToHide = SECONDS_TO_HIDE,
) =>
    notify.open({
        title: <PointsToast title={title} description={description} points={pointsEarned} isBonus={isBonus} />,
        style: {
            ...POINTS_TOAST_STYLES,
            background: getPointsToastBackground(isBonus),
        },
        showProgress: true,
        duration: secondsToHide,
        className: `toast-notification toast-notification--points-${isBonus ? 'bonus' : 'non-bonus'}`,
        pauseOnHover: false,
    });

export const showToast = (
    notify: NotificationInstance, // take from /hooks/useNotify.ts
    title: React.ReactNode | null,
    description: React.ReactNode,
    type: (typeof TOAST_TYPE)[keyof typeof TOAST_TYPE],
    secondsToHide = SECONDS_TO_HIDE,
) =>
    notify.open({
        title: <Toast title={title} description={description} type={type} />,
        style: {
            ...GENERAL_STYLES,
            background: getToastBackground(type),
        },
        showProgress: true,
        duration: secondsToHide,
        className: `toast-notification toast-notification--${type}`,
        icon: null,
        pauseOnHover: false,
    });
